import { Component, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const MICROSOFT_AUTHENTICATOR_AAGUIDS = [
    { label: 'iOS Microsoft Authenticator', id: '90a3ccdf-635c-4729-a248-9b709135078f' },
    { label: 'Android Microsoft Authenticator', id: 'de1e552d-db1d-4423-a619-566b625cdc84' },
];

@Component({
    selector: 'app-fido2-aaguid-list',
    templateUrl: './fido2-aaguid-list.component.html',
    styleUrls: ['./fido2-aaguid-list.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => Fido2AaguidListComponent),
            multi: true,
        },
    ],
})
export class Fido2AaguidListComponent implements ControlValueAccessor {
    value: string[] = [];
    inputValue = '';
    disabled = false;
    inputError = '';

    readonly microsoftAuthenticatorTooltip = MICROSOFT_AUTHENTICATOR_AAGUIDS.map((a) => `${a.label}: ${a.id}`).join(
        '\n',
    );

    onChange: (value: string[]) => void = () => {};
    onTouch: () => void = () => {};

    writeValue(value: string[]): void {
        this.value = Array.isArray(value) ? [...value] : [];
    }

    registerOnChange(fn: typeof this.onChange): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: typeof this.onTouch): void {
        this.onTouch = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    get isValidUuid(): boolean {
        return UUID_REGEX.test(this.inputValue.trim());
    }

    addAaguid(): void {
        const trimmed = this.inputValue.trim();
        if (!trimmed) return;

        if (!UUID_REGEX.test(trimmed)) {
            this.inputError = 'Must be a valid UUID (e.g. 08987058-cadc-4b81-b6e1-30de50dcbe96)';
            return;
        }

        if (this.value.includes(trimmed)) {
            this.inputError = 'This AAGUID has already been added';
            return;
        }

        this.value = [...this.value, trimmed];
        this.onChange(this.value);
        this.onTouch();
        this.inputValue = '';
        this.inputError = '';
    }

    removeAaguid(aaguid: string): void {
        this.value = this.value.filter((id) => id !== aaguid);
        this.onChange(this.value);
        this.onTouch();
    }

    get microsoftAuthenticatorsChecked(): boolean {
        return MICROSOFT_AUTHENTICATOR_AAGUIDS.every((a) => this.value.includes(a.id));
    }

    onMicrosoftAuthenticatorsChange(checked: boolean): void {
        const ids = MICROSOFT_AUTHENTICATOR_AAGUIDS.map((a) => a.id);
        if (checked) {
            const newIds = ids.filter((id) => !this.value.includes(id));
            this.value = [...this.value, ...newIds];
        } else {
            this.value = this.value.filter((id) => !ids.includes(id));
        }
        this.onChange(this.value);
        this.onTouch();
    }

    onInputChange(): void {
        this.inputError = '';
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.addAaguid();
        }
    }
}
